import { STOP_COORDINATES, LINE_COLORS } from "./map-data.js";
import { getLineData, getLineConfig, getState, getActiveConfig } from "./store";
import { getStopName, RETURN_DESTINATIONS } from "./line-config.js";
import { getVisibleLines } from "./utils.js";
import { CFG } from "../data/config.js";
import { TRAIN_STATIONS } from "../data/trains.js";
import { getWalkMinForStop } from "./walk-per-stop";

let map = null;
let markers = {};
let trainMarkers = {};
let stopLineIndex = null; // { stopCode: { lineId: Set<direction> } }
let activeLineFilter = null; // null = mostra tutto, Set = linee selezionate
let filterControl = null;
let activeWalkRadiusCircle = null;
let activeHighlightMarker = null;

export function initMap() {
  // Legacy overlay controls removed in favor of integrated Mappa tab (NAV-3)
}

function computeStopLines() {
  const lineData = getLineData();
  const lineConfig = getLineConfig();
  const stopLines = {};

  for (const stopCode in STOP_COORDINATES) {
    stopLines[stopCode] = {};
  }

  for (const [lineId, lineInfo] of Object.entries(lineData)) {
    for (const scheduleKey in lineInfo) {
      const dest = lineConfig[lineId]?.destination || "Esterna";
      const returnDest = RETURN_DESTINATIONS[lineId] || "Busto G.";
      const direction = scheduleKey.includes("return") ? `Dir. ${returnDest}` : `Dir. ${dest}`;
      const trips = lineInfo[scheduleKey];
      for (const trip of trips) {
        for (const stopCode in trip.stops) {
          if (stopLines[stopCode]) {
            if (!stopLines[stopCode][lineId]) {
              stopLines[stopCode][lineId] = new Set();
            }
            stopLines[stopCode][lineId].add(direction);
          }
        }
      }
    }
  }
  return stopLines;
}

/**
 * Restituisce le linee che servono una fermata.
 */
function getLinesForStop(stopCode) {
  if (!stopLineIndex) return [];
  return Object.keys(stopLineIndex[stopCode] || {});
}

/**
 * Determina se un marker bus deve essere visibile in base al filtro attivo.
 */
function isStopVisible(stopCode) {
  if (!activeLineFilter) return true; // nessun filtro = tutto visibile
  const lines = getLinesForStop(stopCode);
  return lines.some((l) => activeLineFilter.has(l));
}

/**
 * Determina se un marker treno deve essere visibile in base al filtro.
 */
function isTrainStationVisible(stationCode) {
  if (!activeLineFilter) return true;
  return activeLineFilter.has("__trains__");
}

/**
 * Applica il filtro corrente mostrando/nascondendo i marker.
 */
function applyFilter() {
  for (const [code, marker] of Object.entries(markers)) {
    if (isStopVisible(code)) {
      if (!map.hasLayer(marker)) marker.addTo(map);
    } else {
      if (map.hasLayer(marker)) map.removeLayer(marker);
    }
  }
  for (const [code, marker] of Object.entries(trainMarkers)) {
    if (isTrainStationVisible(code)) {
      if (!map.hasLayer(marker)) marker.addTo(map);
    } else {
      if (map.hasLayer(marker)) map.removeLayer(marker);
    }
  }
}

/**
 * Crea il pannello di filtro linee nella mappa.
 */
function createFilterControl() {
  const state = getState();
  const visibleBusLines = getVisibleLines(state, CFG);
  const visibleTrains = state?.settings?.visibleTrains || [];
  const allBusLines = Object.keys(LINE_COLORS).sort();

  // Default: linee visibili nell'app + treni
  activeLineFilter = new Set([...visibleBusLines, "__trains__"]);

  const control = L.control({ position: "topright" });
  control.onAdd = function () {
    const div = L.DomUtil.create("div", "map-filter-panel");
    L.DomEvent.disableClickPropagation(div);
    L.DomEvent.disableScrollPropagation(div);

    div.innerHTML = buildFilterHTML(allBusLines, visibleBusLines);
    bindFilterEvents(div, allBusLines, visibleBusLines);
    return div;
  };
  return control;
}

function buildFilterHTML(allBusLines, visibleBusLines) {
  const trainChecked = activeLineFilter.has("__trains__") ? "checked" : "";

  const lineCheckboxes = allBusLines
    .map((id) => {
      const checked = activeLineFilter.has(id) ? "checked" : "";
      const color = LINE_COLORS[id] || "#888";
      const isDefault = visibleBusLines.includes(id);
      return `<label class="map-filter-line ${isDefault ? "" : "map-filter-other"}" data-line-id="${id}">
      <input type="checkbox" value="${id}" ${checked}>
      <span class="map-filter-dot" style="background:${color};"></span>
      <span>${id}</span>
    </label>`;
    })
    .join("");

  return `
    <div class="map-filter-header">
      <button type="button" class="map-filter-toggle" aria-label="Filtro linee" title="Filtro linee">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
      </button>
    </div>
    <div class="map-filter-body" style="display:none;">
      <div class="map-filter-search">
        <input type="text" placeholder="Cerca linea..." class="map-filter-input">
      </div>
      <div class="map-filter-actions">
        <button type="button" class="map-filter-btn" data-action="all">Tutte</button>
        <button type="button" class="map-filter-btn" data-action="default">Solo preferite</button>
        <button type="button" class="map-filter-btn" data-action="none">Nessuna</button>
      </div>
      <div class="map-filter-list">
        <label class="map-filter-line map-filter-train">
          <input type="checkbox" value="__trains__" ${trainChecked}>
          <span class="map-filter-dot" style="background:#E40314;"></span>
          <span>🚆 Stazioni FS</span>
        </label>
        ${lineCheckboxes}
      </div>
    </div>
  `;
}

function bindFilterEvents(div, allBusLines, visibleBusLines) {
  const toggle = div.querySelector(".map-filter-toggle");
  const body = div.querySelector(".map-filter-body");
  const searchInput = div.querySelector(".map-filter-input");

  toggle.addEventListener("click", () => {
    const isOpen = body.style.display !== "none";
    body.style.display = isOpen ? "none" : "block";
    div.classList.toggle("map-filter-open", !isOpen);
  });

  // Ricerca
  searchInput.addEventListener("input", () => {
    const query = searchInput.value.trim().toUpperCase();
    div.querySelectorAll(".map-filter-line").forEach((label) => {
      const lineId = label.dataset.lineId || "";
      const text = label.textContent.toUpperCase();
      if (!query || text.includes(query) || lineId.includes(query)) {
        label.style.display = "";
      } else {
        label.style.display = "none";
      }
    });
  });

  // Checkbox change
  div.querySelectorAll('.map-filter-list input[type="checkbox"]').forEach((cb) => {
    cb.addEventListener("change", () => {
      if (cb.checked) {
        activeLineFilter.add(cb.value);
      } else {
        activeLineFilter.delete(cb.value);
      }
      applyFilter();
    });
  });

  // Azioni rapide
  div.querySelectorAll("[data-action]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const action = btn.dataset.action;
      if (action === "all") {
        activeLineFilter = new Set([...allBusLines, "__trains__"]);
      } else if (action === "default") {
        activeLineFilter = new Set([...visibleBusLines, "__trains__"]);
      } else if (action === "none") {
        activeLineFilter = new Set();
      }
      // Aggiorna checkbox
      div.querySelectorAll('.map-filter-list input[type="checkbox"]').forEach((cb) => {
        cb.checked = activeLineFilter.has(cb.value);
      });
      applyFilter();
    });
  });
}

export async function openMap(targetStopCode = null) {
  if (window._app_config?.switchTab) {
    window._app_config.switchTab("live-map");
  }
  try {
    const { setMapMode, focusStopOnMap } = await import("./live-map-view.js");
    setMapMode("stops");
    if (targetStopCode) {
      focusStopOnMap(targetStopCode);
    }
  } catch (e) {
    console.warn("[Mappa] Impossibile aprire vista fermate:", e);
  }
}

export function closeMap() {
  // Integrated Mappa tab does not require modal overlay closing
}
